import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import ArticleBody from '../components/research/ArticleBody';
import AuthorBadge from '../components/research/AuthorBadge';
import CategoryPill from '../components/research/CategoryPill';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AudioPlayerBar from '../components/research/AudioPlayerBar';
import { useAuth } from '../contexts/AuthContext';

const ResearchArticlePage = () => {
    const { slug } = useParams();
    const { isAuthenticated } = useAuth();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);

    const API_BASE = 'http://localhost:8000/api/research';

    useEffect(() => {
        const fetchArticle = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${API_BASE}/articles/${slug}/`);
                setArticle(response.data);
            } catch (error) {
                console.error("Error fetching article details:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchArticle();
        window.scrollTo(0, 0);
    }, [slug]);

    // Inject JSON-LD structured data and update page title
    useEffect(() => {
        if (!article) return;
        document.title = article.seo_title || article.title;

        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'article-jsonld';
        script.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.seo_title || article.title,
            "description": article.seo_description || article.excerpt,
            "datePublished": article.published_at,
            "author": { "@type": "Person", "name": article.author_display_name || "TraderBro" },
            "publisher": { "@type": "Organization", "name": "TraderBro Research" },
        });

        // Remove any existing JSON-LD tag before inserting
        document.getElementById('article-jsonld')?.remove();
        document.head.appendChild(script);

        return () => { document.getElementById('article-jsonld')?.remove(); };
    }, [article]);

    if (loading) return (
        <div className="min-h-screen bg-white">
            <Navigation />
            <div className="pt-32 max-w-4xl mx-auto px-4">
                <div className="h-8 w-24 bg-gray-100 rounded-full animate-pulse mb-6" />
                <div className="h-16 w-3/4 bg-gray-100 rounded-2xl animate-pulse mb-8" />
                <div className="h-12 w-48 bg-gray-100 rounded-full animate-pulse" />
            </div>
        </div>
    );
    if (!article) return <div className="min-h-screen bg-white pt-32 text-center">Article not found</div>;

    return (
        <div className="flex flex-col min-h-screen bg-white">
            <Navigation />
            <main className="flex-1 pb-24">
                {/* Article Header / Hero */}
                <div className="bg-[#F9FAFB] pt-32 pb-16 border-b border-gray-100">
                    <div className="max-w-4xl mx-auto px-4">
                        <div className="flex items-center gap-3 mb-6">
                            <CategoryPill category={article.category} />
                            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
                                {new Date(article.published_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
                            </span>
                        </div>

                        <h1 className="text-4xl md:text-5xl font-black text-[#222425] leading-tight mb-8">
                            {article.title}
                        </h1>

                        <div className="flex flex-wrap items-center justify-between gap-6">
                            <AuthorBadge
                                type={article.author_type}
                                name={article.author_display_name}
                                role={article.author_role}
                            />

                            <div className="flex items-center gap-3">
                                {article.audio_status === 'ready' && (
                                    <button
                                        onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                                        className="flex items-center gap-2 bg-[#00DA76] text-white px-5 py-2.5 rounded-full font-bold text-sm shadow-lg shadow-[#00DA76]/20 transition-all hover:scale-105 active:scale-95"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15L4 14.414V9.586L5.586 9H10l5-5v16l-5-5H5.586z" />
                                        </svg>
                                        Listen Analysis
                                    </button>
                                )}
                                <button className="p-2.5 rounded-full border border-gray-200 text-gray-400 hover:text-[#00DA76] hover:border-[#00DA76] transition-all">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 mt-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

                        {/* Main Content Area */}
                        <div className="lg:col-span-8 lg:col-start-2">
                            {/* Body with optional paywall overlay */}
                            <div className="relative">
                                <ArticleBody markdown={article.body} />
                                {article.is_truncated && (
                                    <>
                                        {/* Gradient fade */}
                                        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                                        {/* Signup CTA */}
                                        <div className="relative mt-4 bg-gray-50 rounded-3xl p-10 text-center border border-gray-200 shadow-sm">
                                            <div className="w-12 h-12 bg-[#00DA76]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                                <svg className="w-6 h-6 text-[#00DA76]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                            </div>
                                            <h3 className="text-2xl font-black text-[#222425] mb-3">
                                                Continue Reading
                                            </h3>
                                            <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                                                Sign up for free to read the full analysis and access all TraderBro research reports.
                                            </p>
                                            <div className="flex items-center justify-center gap-3">
                                                <Link
                                                    to="/register"
                                                    className="px-8 py-3 bg-[#00DA76] text-white rounded-xl font-bold hover:opacity-90 transition shadow-lg shadow-[#00DA76]/20"
                                                >
                                                    Sign Up Free
                                                </Link>
                                                <Link
                                                    to="/login"
                                                    className="px-8 py-3 bg-white text-gray-700 rounded-xl font-bold border border-gray-200 hover:border-gray-400 transition"
                                                >
                                                    Log In
                                                </Link>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {!article.is_truncated && (
                            <div className="mt-16 pt-10 border-t border-gray-100 italic text-gray-400 text-sm">
                                Disclaimer: This report is for informational purposes only and does not constitute financial advice. Always consult with a certified professional before making investment decisions.
                            </div>
                            )}

                            {!article.is_truncated && (
                            <div className="mt-12 bg-gray-50 rounded-3xl p-8 flex items-center justify-between">
                                <div>
                                    <h3 className="text-xl font-black text-[#222425] mb-2">Have questions about this?</h3>
                                    <p className="text-gray-500">Ask our AI for specific stock metrics or outlooks.</p>
                                </div>
                                <Link to="/chat" className="bg-[#222425] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#00DA76] transition-all">
                                    Ask AI Assistant
                                </Link>
                            </div>
                            )}
                        </div>

                        {/* Sidebar Area (Desktop only) */}
                        <div className="hidden lg:block lg:col-span-3">
                            <div className="sticky top-32 space-y-10">
                                {/* Related Tickers */}
                                {article.related_tickers?.length > 0 && (
                                    <div className="p-6 bg-[#F9FAFB] rounded-3xl border border-gray-100">
                                        <h3 className="text-xs font-black uppercase tracking-widest text-[#222425] mb-6">Related Tickers</h3>
                                        <div className="space-y-4">
                                            {article.related_tickers.map(ticker => (
                                                <Link
                                                    key={ticker}
                                                    to={`/screener?ticker=${ticker}`}
                                                    className="flex items-center justify-between group cursor-pointer"
                                                >
                                                    <span className="text-sm font-bold group-hover:text-[#00DA76] transition-colors">{ticker}</span>
                                                    <div className="flex items-center gap-1.5 opacity-50">
                                                        <div className="w-12 h-6 bg-gray-200 rounded-sm"></div>
                                                        <span className="text-[10px] font-bold">Details</span>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>

                {/* Floating Audio Player */}
                {article.audio_status === 'ready' && article.audio_file && (
                    <AudioPlayerBar
                        audioUrl={article.audio_file.startsWith('http') ? article.audio_file : `http://localhost:8000${article.audio_file}`}
                        title={article.title}
                        durationSec={article.audio_duration_sec}
                    />
                )}
            </main>
            <Footer />
        </div>
    );
};

export default ResearchArticlePage;
